<template>
  <div class="c-field-mapping-content">
    <BittsCard :title-text="columnHeaderText" :no-padding="true">
      <template #title>
        <div class="flex w-1/2 items-center">
          <BittsSvg svg="logoNoText" class="mr-6 h-12 w-12" />
          <span class="text-neutral-text-strong font-bold">
            Crossbeam Fields
          </span>
        </div>
        <div class="flex items-center w-1/2 pl-16">
          <BittsSvg
            :svg="sourceType === 'csv' ? 'csvIcon' : 'googleSheetsIcon'"
            class="mr-6 h-12 w-12"
          />
          <span class="text-neutral-text-strong font-bold">
            {{ sourceType === 'csv' ? 'CSV' : 'Google Sheets' }} Fields
          </span>
        </div>
      </template>
      <template v-for="field in allFields" :key="field.label">
        <FieldMapping :label="field.label" :level="field.level">
          <template #default>
            <div class="flex flex-col gap-8 flex-1 field-select-wrapper">
              <BittsSelect
                :aria-label="`Column for ${field.label}`"
                class="field-select"
                :model-value="getSelectedColumnForField(field)"
                @update:model-value="updateField(field, $event)"
                :options="getAvailableColumnsFor(field)"
                placeholder="Select a field"
                :use-disabled-options="true"
                :allow-clear="true"
              />
            </div>
          </template>
        </FieldMapping>
      </template>
    </BittsCard>
  </div>
</template>

<script setup lang="ts">
import { BittsCard, BittsSelect, BittsSvg } from '@crossbeam/bitts';

import { computed, watch } from 'vue';

import FieldMapping from '@/components/data-sources/FieldMapping.vue';

type FieldLevel = 'required' | 'recommended' | 'optional';

type FieldObject = 'main' | 'owner';

type FieldDefinition = {
  label: string;
  level: FieldLevel;
  mdmProperty: string;
  object: FieldObject;
  placeholder?: string;
};

type ColumnOption = {
  label: string;
  value: string;
};

type Props = {
  sourceType: 'csv' | 'googlesheet';
  uploadType: 'account' | 'lead';
  availableColumns: ColumnOption[];
  modelValue: Record<string, FieldDefinition | null>;
};

const props = defineProps<Props>();
const emit = defineEmits<{
  'update:modelValue': [value: Record<string, FieldDefinition | null>];
}>();

const AccountFields: FieldDefinition[] = [
  {
    label: 'Company Name',
    level: 'required',
    mdmProperty: 'company_name',
    object: 'main',
  },
  {
    label: 'Company Website',
    level: 'recommended',
    mdmProperty: 'company_website',
    object: 'main',
  },
  {
    label: 'Account Owner Name',
    level: 'recommended',
    mdmProperty: 'person_name',
    object: 'owner',
  },
  {
    label: 'Account Owner Email',
    level: 'recommended',
    mdmProperty: 'person_email',
    object: 'owner',
  },
  {
    label: 'DUNS Number',
    level: 'optional',
    mdmProperty: 'company_duns_number',
    object: 'main',
  },
  {
    label: 'Industry',
    level: 'optional',
    mdmProperty: 'company_industry',
    object: 'main',
  },
  {
    label: 'Number of Employees',
    level: 'optional',
    mdmProperty: 'company_employees',
    object: 'main',
  },
  {
    label: 'Account Owner Phone',
    level: 'optional',
    mdmProperty: 'person_phone',
    object: 'owner',
  },
  {
    label: 'Country',
    level: 'optional',
    mdmProperty: 'company_country',
    object: 'main',
  },
  {
    label: 'Postal Code',
    level: 'optional',
    mdmProperty: 'company_postal_code',
    object: 'main',
  },
  {
    label: 'Region',
    level: 'optional',
    mdmProperty: 'company_region',
    object: 'main',
  },
  {
    label: 'City',
    level: 'optional',
    mdmProperty: 'company_city',
    object: 'main',
  },
  {
    label: 'Address',
    level: 'optional',
    mdmProperty: 'company_address',
    object: 'main',
  },
  {
    label: 'Company Phone Number',
    level: 'optional',
    mdmProperty: 'company_phone_number',
    object: 'main',
  },
];

const LeadFields: FieldDefinition[] = [
  {
    label: 'Email',
    level: 'required',
    mdmProperty: 'person_email',
    object: 'main',
  },
  {
    label: 'Account Owner Name',
    level: 'recommended',
    mdmProperty: 'person_name',
    object: 'owner',
  },
  {
    label: 'Account Owner Email',
    level: 'recommended',
    mdmProperty: 'person_email',
    object: 'owner',
  },
  {
    label: 'Lead Name',
    level: 'optional',
    mdmProperty: 'person_name',
    object: 'main',
  },
  {
    label: 'Lead Phone',
    level: 'optional',
    mdmProperty: 'person_phone',
    object: 'main',
  },
  {
    label: 'Lead Title',
    level: 'optional',
    mdmProperty: 'person_title',
    object: 'main',
  },
  {
    label: 'Company Name',
    level: 'optional',
    mdmProperty: 'company_name',
    object: 'main',
  },
  {
    label: 'Company Website',
    level: 'optional',
    mdmProperty: 'company_website',
    object: 'main',
  },
  {
    label: 'Country',
    level: 'optional',
    mdmProperty: 'company_country',
    object: 'main',
  },
  {
    label: 'Postal Code',
    level: 'optional',
    mdmProperty: 'company_postal_code',
    object: 'main',
  },
  {
    label: 'Region',
    level: 'optional',
    mdmProperty: 'company_region',
    object: 'main',
  },
  {
    label: 'City',
    level: 'optional',
    mdmProperty: 'company_city',
    object: 'main',
  },
  {
    label: 'Address',
    level: 'optional',
    mdmProperty: 'company_address',
    object: 'main',
  },
  {
    label: 'Account Owner Phone',
    level: 'optional',
    mdmProperty: 'person_phone',
    object: 'owner',
  },
];

const allFields = computed(() => {
  return props.uploadType === 'account' ? AccountFields : LeadFields;
});

const FIELD_ALIASES: Record<string, string[]> = {
  // shared
  'Company Name': [
    'company name',
    'company',
    'account name',
    'account',
    'organization',
    'org',
    'business',
    'business name',
    'customer',
    'customer name',
  ],
  'Company Website': [
    'company website',
    'website',
    'website name',
    'domain',
    'url',
    'web',
    'site',
    'homepage',
    'web address',
  ],
  'Account Owner Name': [
    'account owner name',
    'account owner',
    'owner name',
    'owner',
    'account executive name',
    'account executive',
    'ae',
    'ae name',
    'rep',
    'rep name',
    'sales rep',
    'csm',
    'csm name',
  ],
  'Account Owner Email': [
    'account owner email',
    'owner email',
    'account executive email',
    'ae email',
    'rep email',
    'sales rep email',
    'csm email',
    'email',
  ],
  'Account Owner Phone': [
    'account owner phone',
    'owner phone',
    'account executive phone',
    'ae phone',
    'rep phone',
    'sales rep phone',
  ],
  Country: ['country'],
  'Postal Code': ['postal code', 'postcode', 'zip', 'zip code', 'postal'],
  Region: ['region', 'state', 'province'],
  City: ['city', 'town'],
  Address: ['address', 'street', 'street address', 'address line', 'addr'],
  // account-only
  'DUNS Number': ['duns', 'duns number', 'company duns number'],
  Industry: ['industry', 'sector', 'vertical'],
  'Number of Employees': [
    'employees',
    'employee count',
    'number of employees',
    'num employees',
    'headcount',
    'company size',
  ],
  'Company Phone Number': [
    'company phone number',
    'company phone',
    'business phone',
  ],
  // lead-only
  Email: ['email', 'email address', 'e-mail', 'work email'],
  'Lead Name': ['lead name', 'name', 'full name'],
  'Lead Phone': [
    'lead phone',
    'contact phone',
    'mobile',
    'mobile phone',
    'cell',
    'cell phone',
  ],
  'Lead Title': ['title', 'job title', 'position', 'role'],
};

function normalizeHeader(s: string): string {
  return s.toLowerCase().replace(/[_\s-]+/g, '');
}

function computeAutoFillMappings(
  columns: ColumnOption[],
  fields: FieldDefinition[],
): Record<string, FieldDefinition> {
  const result: Record<string, FieldDefinition> = {};
  const claimed = new Set<string>();
  for (const field of fields) {
    const aliases = (FIELD_ALIASES[field.label] ?? []).map(normalizeHeader);
    if (!aliases.length) continue;
    const match = columns.find(
      (col) =>
        !claimed.has(col.value) && aliases.includes(normalizeHeader(col.value)),
    );
    if (match) {
      result[match.value] = field;
      claimed.add(match.value);
    }
  }
  return result;
}

watch(
  () => props.availableColumns,
  (columns) => {
    if (!columns?.length) return;
    const isEmpty = Object.keys(props.modelValue).length === 0;
    if (!isEmpty) return;
    const autoFilled = computeAutoFillMappings(columns, allFields.value);
    if (Object.keys(autoFilled).length > 0) {
      emit('update:modelValue', autoFilled);
    }
  },
  { immediate: true },
);

const fieldMappings = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value),
});

function updateField(field: FieldDefinition, columnName: string | null) {
  const updated = Object.fromEntries(
    Object.entries(fieldMappings.value).filter(
      ([_, fieldDef]) => fieldDef?.label !== field.label,
    ),
  );

  if (columnName) {
    updated[columnName] = field;
  }

  emit('update:modelValue', updated);
}

function getSelectedColumnForField(field: FieldDefinition): string | null {
  const entry = Object.entries(fieldMappings.value).find(
    ([_, fieldDef]) => fieldDef?.label === field.label,
  );
  return entry ? entry[0] : null;
}

const columnHeaderText = computed(() =>
  props.sourceType === 'csv' ? 'CSV' : 'Google Sheet',
);

function getAvailableColumnsFor(field: FieldDefinition) {
  const currentSelection = getSelectedColumnForField(field);
  const selectedColumns = Object.keys(fieldMappings.value).filter(
    (key) => fieldMappings.value[key] !== null,
  );

  return props.availableColumns.map((col) => ({
    ...col,
    disabled:
      selectedColumns.includes(col.value) && col.value !== currentSelection,
  }));
}
</script>
<style lang="pcss" scoped>
@reference "../../css/theme.css";
.header {
  @apply font-bold text-neutral-text-strong;
}
.field-select-wrapper {
  @apply w-full;
}
.field-select {
  @apply w-full;
}
.c-field-mapping-content {
  @apply flex flex-col gap-8;
  :deep(.bitts-container) {
    @apply rounded-[10px];
  }
  :deep(.bitts-card__title) {
    @apply bg-neutral-background-weak py-8 rounded-tr-[10px] rounded-tl-[10px] h-32;
  }
  :deep(.c-bitts-divider) {
    @apply hidden!;
  }
}
</style>
